// modules/events.js — Podujatia (event + kapacita + registrácie). Reálna SK vertikála (workshop,
// kurz, webinár): event = records riadok (module='event') s dátumom/kapacitou/cenou vstupenky;
// registrácia = records riadok (module='event_registration') viazaný na event + účastníka.
// NEpridáva tabuľku (vzor sklad/rental) — 0 fetch / 0 LLM / 0 DDL.
//
// STAV eventu: deep-frozen automat (vzor rental/absence, tx read-guard-write, injektovaný čas, audit)
// open → closed → open (reopen) / cancelled. cancelled TERMINÁLNY. Registrovať možno LEN keď open.
//
// KAPACITA (double-booking trieda, vzor rental): event má strop miest. registerAttendee overuje
// count živých registrácií < capacity VNÚTRI db.transaction (engine mutex serializuje) → na jednom
// zariadení sa strop nepretečie ani pri súbehu. capacity=null = neobmedzené. (Cross-device súbeh je
// soft konflikt sync vrstvy — rovnaká TOCTOU trieda ako rental/absence, LWW + sync_conflicts.)
//
// REGISTRÁCIA IDEMPOTENTNÁ: deterministické id z hash(JSON-tuple [event_ref, attendee_ref]) — vzor
// absence. Ten istý účastník na ten istý event = jedna registrácia (konkatenácia by zlúčila rôzne
// dvojice — data-loss pasca, viď timesheet/absence deterministic-id). Zrušená → dá sa znova (revive),
// ale revive znova prejde kapacitou (slot sa medzitým mohol obsadiť).
//
// CENA VSTUPENKY (money): int centy, SNAPSHOTNUTÁ do registrácie pri registrácii — neskoršia zmena
// ceny eventu spätne nemení, čo účastník dlží. registrationFeeCents berie hodnotu VÝHRADNE zo stampu
// registrácie, žiadny override argument (rental endIso pasca: explicitný koniec prebil audit stamp).
//
// ČAS: výhradne injektovaný deps.now() — žiadny ambientný čas v zdroji. Peniaze = int centy.
import { db, newCommonFields } from '../db';
import { sha256Hex } from '../migration/dedupe';
import { isValidIsoDate } from '../dateUtils';

// ─── MODULE_DEF descriptor ────────────────────────────────────────────────────
export const MODULE_DEF = {
  module: 'event',
  label_sk: 'Podujatia',
  // accent = jediný zdroj brand farby modulu (menu+header+KPI).
  accent: 'yellow',
  entity: {
    base: 'record',
    custom_fields: [
      { stable_id: 'f_title', label: 'Názov podujatia', type: 'text' },
      { stable_id: 'f_date', label: 'Dátum', type: 'date' },
      { stable_id: 'f_capacity', label: 'Kapacita (miest)', type: 'number' },
      { stable_id: 'f_price', label: 'Cena vstupenky (€)', type: 'number' },
    ],
  },
  view: { filter: { module: 'event' }, sort: 'event_date_asc' },
  access: { collaborator: true, max_tier_out: 2 },
  metrics: [
    { slug: 'otvorene_podujatia', reduce: 'count', filter: { module: 'event', status: 'open' } },
    { slug: 'registracie_spolu', reduce: 'count', filter: { module: 'event_registration', active: true } },
  ],
};

export const EVENT_MODULE = 'event';
export const EVENT_REGISTRATION_MODULE = 'event_registration';

export const EVENT_STATUSES = Object.freeze(['open', 'closed', 'cancelled']);
export const TERMINAL_EVENT_STATUSES = Object.freeze(['cancelled']);
export const EVENT_TRANSITIONS = Object.freeze({
  open: Object.freeze(['closed', 'cancelled']),
  closed: Object.freeze(['open', 'cancelled']), // reopen registrácie / zrušiť
  cancelled: Object.freeze([]),
});

export const REGISTRATION_STATUSES = Object.freeze(['registered', 'cancelled']);

// ─── Chyby ────────────────────────────────────────────────────────────────────
export class EventError extends Error {
  constructor(code, message) {
    super(message || code);
    this.name = 'EventError';
    this.code = code;
  }
}
const fail = (code, message) => { throw new EventError(code, message); };

// ─── Validátory ───────────────────────────────────────────────────────────────
const isCents = (v) => Number.isInteger(v) && v >= 0;
const isNonEmpty = (v) => typeof v === 'string' && v.trim() !== '';
// capacity: null (neobmedzené) alebo celé kladné číslo miest.
const isCapacity = (v) => v === null || (Number.isInteger(v) && v >= 1);
function assertDeps(deps) {
  if (typeof deps?.now !== 'function') fail('E_INVALID_INPUT', 'deps.now je povinná funkcia (injektovaný čas)');
}

// Deterministické id registrácie z (event_ref, attendee_ref). Hash Z JSON-tuple — injektívne
// (konkatenácia by zlúčila rôzne dvojice; sha256 hex = safe id chars). sha256Hex je synchrónny.
export function registrationId(eventRef, attendeeRef) {
  return `evreg-${sha256Hex(JSON.stringify([eventRef, attendeeRef]))}`;
}

// Živá registrácia (registered, nezmazaná) pre daný event.
const isLiveRegistered = (r, eventRef) =>
  r != null && r.module === EVENT_REGISTRATION_MODULE && r.deleted_at == null &&
  r.custom?.status === 'registered' && r.custom?.event_ref === eventRef;

async function getLiveEvent(id, store) {
  if (!isNonEmpty(id)) fail('E_NOT_FOUND', 'Podujatie neexistuje alebo je zmazané');
  const rec = await store.get(id);
  if (!rec || rec.module !== EVENT_MODULE || rec.deleted_at != null) {
    fail('E_NOT_FOUND', 'Podujatie neexistuje alebo je zmazané');
  }
  return rec;
}

// ─── createEvent — nový 'open' event ──────────────────────────────────────────
// input = { title (povinné), event_date (ISO povinné), capacity (int≥1 alebo null=neobmedzené),
//           price_cents? (int centy ≥0, default 0 = zdarma) }
export async function createEvent(input, deps) {
  assertDeps(deps);
  if (!isNonEmpty(input?.title)) fail('E_INVALID_INPUT', 'title je povinný neprázdny string');
  if (!isValidIsoDate(input?.event_date)) fail('E_INVALID_INPUT', 'event_date musí byť valídny ISO dátum (YYYY-MM-DD)');
  const capacity = input.capacity === undefined ? null : input.capacity;
  if (!isCapacity(capacity)) fail('E_INVALID_INPUT', 'capacity musí byť celé kladné číslo miest alebo null (neobmedzené)');
  const priceCents = input.price_cents == null ? 0 : input.price_cents;
  if (!isCents(priceCents)) fail('E_INVALID_INPUT', 'price_cents musí byť celé nezáporné číslo v centoch');

  const at = deps.now();
  const row = {
    ...newCommonFields(),
    module: EVENT_MODULE,
    status: 'open',
    entry_date: at.slice(0, 10),
    created_at: at,
    updated_at: at,
    custom: {
      title: input.title.trim(),
      event_date: input.event_date,
      capacity,
      price_cents: priceCents,
      status_history: [{ from: null, to: 'open', at, kind: 'create' }],
    },
  };
  await db.records.add(row);
  return row;
}

// ─── Jadro prechodu eventu — read-guard-write v tx ────────────────────────────
async function transitionEvent(id, toStatus, meta, deps) {
  assertDeps(deps);
  return db.transaction(async (tx) => {
    const rec = await getLiveEvent(id, tx.records);
    const from = rec.status;
    if (TERMINAL_EVENT_STATUSES.includes(from)) fail('E_TERMINAL_STATUS', `Status '${from}' je terminálny`);
    if (!(EVENT_TRANSITIONS[from] || []).includes(toStatus)) {
      fail('E_INVALID_TRANSITION', `Prechod '${from}'→'${toStatus}' nie je definovaná hrana`);
    }
    const at = deps.now();
    const entry = { from, to: toStatus, at, kind: 'transition' };
    if (isNonEmpty(meta?.note)) entry.note = meta.note;
    const custom = {
      ...(rec.custom || {}),
      status_history: [...(rec.custom?.status_history || []), entry],
    };
    const rev = (rec.rev || 1) + 1;
    await tx.records.update(id, { status: toStatus, custom, updated_at: at, rev });
    return { ...rec, status: toStatus, custom, updated_at: at, rev };
  });
}

export async function closeEvent(id, meta = {}, deps) {
  return transitionEvent(id, 'closed', meta, deps);
}
export async function reopenEvent(id, meta = {}, deps) {
  return transitionEvent(id, 'open', meta, deps);
}
export async function cancelEvent(id, meta = {}, deps) {
  return transitionEvent(id, 'cancelled', meta, deps);
}

// ─── registerAttendee — kapacita guard + idempotentná registrácia (v TX) ──────
// input = { event_ref (povinné), attendee_ref (povinné) }. Cena sa snapshotne z eventu.
export async function registerAttendee(input, deps) {
  assertDeps(deps);
  const eventRef = input?.event_ref;
  const attendeeRef = input?.attendee_ref;
  if (!isNonEmpty(eventRef)) fail('E_INVALID_INPUT', 'event_ref je povinný neprázdny string');
  if (!isNonEmpty(attendeeRef)) fail('E_INVALID_INPUT', 'attendee_ref je povinný neprázdny string');
  const id = registrationId(eventRef, attendeeRef);

  return db.transaction(async (tx) => {
    const event = await getLiveEvent(eventRef, tx.records); // E_NOT_FOUND ak event neexistuje
    if (event.status !== 'open') fail('E_EVENT_NOT_OPEN', `Podujatie nie je otvorené (status '${event.status}')`);

    const existing = await tx.records.get(id);
    if (existing && existing.module === EVENT_REGISTRATION_MODULE
      && existing.deleted_at == null && existing.custom?.status === 'registered') {
      return existing; // živá registrácia — idempotent, NEkonzumuje ďalší slot
    }

    // Fresh ALEBO revive zrušenej → konzumuje slot → kapacita guard VNÚTRI tx (mutex serializuje).
    const capacity = event.custom?.capacity;
    if (capacity != null) {
      const live = await tx.records.filter((r) => isLiveRegistered(r, eventRef)).toArray();
      if (live.length >= capacity) fail('E_EVENT_FULL', 'Podujatie je plné (kapacita vyčerpaná)');
    }

    const at = deps.now();
    const priceCents = isCents(event.custom?.price_cents) ? event.custom.price_cents : 0;
    const entry = { to: 'registered', at, kind: existing ? 'reregister' : 'register' };

    if (existing && existing.module === EVENT_REGISTRATION_MODULE) {
      // Revive zrušenej/zmazanej registrácie — rovnaké deterministické id (add() by narazil na PK).
      const custom = {
        ...(existing.custom || {}),
        event_ref: eventRef, attendee_ref: attendeeRef,
        status: 'registered', price_cents: priceCents,
        status_history: [...(existing.custom?.status_history || []), entry],
      };
      const rev = (existing.rev || 1) + 1;
      await tx.records.update(id, { deleted_at: null, status: 'active', custom, updated_at: at, rev });
      return { ...existing, deleted_at: null, status: 'active', custom, updated_at: at, rev };
    }

    const row = {
      ...newCommonFields(),
      id, // deterministické — prepíše uuid
      module: EVENT_REGISTRATION_MODULE,
      status: 'active', // živosť riadku (registračný stav je v custom.status)
      created_at: at,
      updated_at: at,
      custom: {
        event_ref: eventRef,
        attendee_ref: attendeeRef,
        status: 'registered',
        price_cents: priceCents,
        status_history: [entry],
      },
    };
    await tx.records.add(row);
    return row;
  });
}

// ─── cancelRegistration — uvoľní slot (v TX) ──────────────────────────────────
// input = { event_ref, attendee_ref }. Idempotentné cez deterministické id.
export async function cancelRegistration(input, deps) {
  assertDeps(deps);
  const eventRef = input?.event_ref;
  const attendeeRef = input?.attendee_ref;
  if (!isNonEmpty(eventRef)) fail('E_INVALID_INPUT', 'event_ref je povinný neprázdny string');
  if (!isNonEmpty(attendeeRef)) fail('E_INVALID_INPUT', 'attendee_ref je povinný neprázdny string');
  const id = registrationId(eventRef, attendeeRef);

  return db.transaction(async (tx) => {
    const rec = await tx.records.get(id);
    if (!rec || rec.module !== EVENT_REGISTRATION_MODULE || rec.deleted_at != null
      || rec.custom?.status !== 'registered') {
      fail('E_NOT_FOUND', 'Registrácia neexistuje alebo už bola zrušená');
    }
    const at = deps.now();
    const entry = { from: 'registered', to: 'cancelled', at, kind: 'cancel' };
    const custom = {
      ...(rec.custom || {}),
      status: 'cancelled',
      status_history: [...(rec.custom?.status_history || []), entry],
    };
    const rev = (rec.rev || 1) + 1;
    await tx.records.update(id, { custom, updated_at: at, rev });
    return { ...rec, custom, updated_at: at, rev };
  });
}

// ─── Čisté funkcie ────────────────────────────────────────────────────────────

// FINÁLNY poplatok registrácie — VÝHRADNE zo snapshotnutého price_cents (stamp pri registrácii).
// Zrušená registrácia nič nedlží → null. Žiadny override argument — explicitná cena by prebila
// snapshot a fabrikovala účet (money diera, vzor rental returned_date REGRESIA).
export function registrationFeeCents(registration) {
  if (registration == null || registration.deleted_at != null) return null; // soft-deleted nič nedlží
  const c = registration.custom;
  if (!c || c.status !== 'registered') return null;
  return isCents(c.price_cents) ? c.price_cents : null;
}

// Živé registrované registrácie daného eventu.
export function liveRegistrations(records, eventRef) {
  const recs = Array.isArray(records) ? records : [];
  return recs.filter((r) => isLiveRegistered(r, eventRef));
}

// Počet živých registrácií eventu (kapacita reduce).
export function registrationCount(records, eventRef) {
  return liveRegistrations(records, eventRef).length;
}

// Zostávajúca kapacita = capacity − count; neobmedzený event (capacity null) → null.
export function remainingCapacity(event, records) {
  const capacity = event?.custom?.capacity;
  if (capacity == null) return null;
  return capacity - registrationCount(records, event.id);
}

// Je event plný? count ≥ capacity. Neobmedzený (null) nikdy nie je plný.
export function isEventFull(event, records) {
  const capacity = event?.custom?.capacity;
  if (capacity == null) return false;
  return registrationCount(records, event.id) >= capacity;
}

// Tržba eventu = Σ snapshotnutých cien živých registrovaných (honest: zrušené nerátajú, žiadna
// fabrikácia z aktuálnej ceny eventu). Zaokrúhlenie nie je — price_cents je už int centy.
export function eventRevenueCents(records, eventRef) {
  const recs = Array.isArray(records) ? records : [];
  let cents = 0;
  for (const r of recs) {
    if (!isLiveRegistered(r, eventRef)) continue;
    if (isCents(r.custom?.price_cents)) cents += r.custom.price_cents;
  }
  return cents;
}
